import { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputMask from "react-input-mask";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { ClientContext } from "../../state/ClientContext";
import { AuthContext } from "../../state/AuthContext";
import "./ClientDetailsForm.css";
import { toast } from "react-toastify";
import { apiRequest } from "../../api/baseApi";
import { US_TIMEZONES, US_STATES } from "../../util/defs";
import { getFirstName, getLastName } from "../../utils/fieldAccessors";
import {
  formatClientIanaTzReadable,
  isValidIanaZone,
} from "../../utils/activityTimezone";
import { canEditClientDetails } from "../../utils/clientProfileAccess";

export default function ClientDetailsForm() {
  const { selectedClient, refetchClients } = useContext(ClientContext);
  const [isEditable, setIsEditable] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { currentUser } = useContext(AuthContext);
  const canEdit = canEditClientDetails(currentUser);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: getFirstName(selectedClient, ""),
      lastName: getLastName(selectedClient, ""),
      idNumber: selectedClient?.idNumber || "",
      email: selectedClient?.email || "",
      phone: selectedClient?.phone || "",
      city: selectedClient?.city || "",
      state: selectedClient?.state || "",
      address: selectedClient?.address || "",
      zipCode: selectedClient?.zipCode || "",
      timezone: selectedClient?.timezone || "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Enter first name."),
      lastName: Yup.string().required("Enter last name."),
      idNumber: Yup.string().required("Enter ID number."),
      email: Yup.string().email("Enter valid email.").required("Enter email."),
      phone: Yup.string()
        .required("Enter phone number.")
        .matches(/^\+1\d{10}$/, "Enter valid U.S. phone number."),
      city: Yup.string().required("Enter city."),
      state: Yup.string().required("Select state."),
      address: Yup.string().required("Enter valid address."),
      zipCode: Yup.string()
        .required("Enter ZIP code.")
        .matches(/^\d{5}$/, "ZIP code must be 5 digits."),
      timezone: Yup.string()
        .required("Select timezone.")
        .test(
          "iana-timezone",
          "Pick a valid IANA timezone from the list.",
          (val) => typeof val === "string" && isValidIanaZone(val),
        ),
    }),
    onSubmit: async (values) => {
      if (!canEdit) {
        toast.error(
          "Only Caregiver Admins can edit client information. Ask a Caregiver Admin to make changes.",
        );
        return;
      }
      // Email is display-only (disabled input) and is not in UpdateUserRequest —
      // omit so OpenAPI stays strict-friendly (#469).
      const { email: _email, ...updateBody } = values;
      try {
        const response = await apiRequest(`/users/${selectedClient.id}`, {
          version: "v2",
          method: "PUT",
          body: updateBody,
        });
        if (response.ok) {
          toast.success("Update profile success");
          setIsEditable(false);
          try {
            await refetchClients();
          } catch (refreshError) {
            console.error("Failed to refresh clients after profile update", refreshError);
            toast.error(
              "Profile saved, but the client list could not be refreshed. Reload the page.",
            );
          }
          return;
        }
        let serverMessage = "";
        try {
          const payload = await response.json();
          serverMessage = payload?.error || payload?.message || "";
        } catch (_ignored) {
          // non-JSON body
        }
        toast.error(
          serverMessage ||
            `Failed to update profile (HTTP ${response.status}).`,
        );
        console.error("Failed to update profile", response.status, serverMessage);
      } catch (error) {
        toast.error(error?.message || "Failed to update profile");
        console.error("Failed to update profile", error);
      }
    },
  });

  // GMA-1916: switching clients must not leak a previous client's expanded
  // OR editable card into the newly selected one — exiting edit mode here
  // mirrors the canEdit-revoked effect below (Copilot review on #579:
  // isEditable alone was left true, so the new client rendered fully
  // editable with the prior client's in-progress edits still attached).
  useEffect(() => {
    setIsExpanded(false);
    setIsEditable(false);
    formik.resetForm();
    // formik identity is unstable; only react to the selected client changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on client switch only
  }, [selectedClient?.id]);

  // Caregivers must never stay in edit mode — clamp + discard dirty values
  // when permission is revoked mid-session (treat as cancel).
  useEffect(() => {
    if (!canEdit) {
      setIsEditable(false);
      formik.resetForm();
    }
    // formik identity is unstable; only react to canEdit flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on role revoke only
  }, [canEdit]);

  if (!selectedClient) {return null;}

  const primaryFields = [
    { label: "First Name", name: "firstName", type: "text" },
    { label: "Last Name", name: "lastName", type: "text" },
    { label: "ID Number", name: "idNumber", type: "text" },
  ];

  // GMA-1916: everything past ID Number is hidden behind Show more so the
  // card reads First / Last / ID by default.
  const collapsibleFields = [
    { label: "Email", name: "email", type: "email", disabled: true },
    { label: "Phone", name: "phone", type: "text", mask: "+19999999999" },
    { label: "City", name: "city", type: "text" },
    { label: "State", name: "state", type: "select" },
    { label: "Address", name: "address", type: "text" },
    { label: "Zip Code", name: "zipCode", type: "text" },
    { label: "Timezone", name: "timezone", type: "select" },
  ];

  const editing = canEdit && isEditable;
  // Editing requires every field to validate (Yup schema covers all ten), so
  // entering edit mode always shows the full card regardless of toggle state.
  const showCollapsibleFields = editing || isExpanded;
  // When permission is revoked, read-only spans must not flash dirty Formik
  // values for one paint before resetForm() runs in useEffect.
  const displayValues = canEdit ? formik.values : formik.initialValues;

  return (
    <div>
      <form
        className="client-form"
        data-testid="client-details-form"
        aria-describedby={canEdit ? undefined : "client-details-readonly-notice"}
        onSubmit={(ev) => {
          if (!canEdit) {
            ev.preventDefault();
            return;
          }
          formik.handleSubmit(ev);
        }}
      >
        <div className="client-form-header">
          <h1>Client details</h1>

          <div className="button-group">
            {canEdit ? (
              <button
                type="button"
                aria-label={editing ? "Cancel editing" : "Edit client details"}
                onClick={(ev) => {
                  ev.preventDefault();
                  if (editing) {
                    formik.resetForm();
                  }
                  setIsEditable(!isEditable);
                }}
              >
                {!editing ? <EditIcon /> : <CloseIcon />}
              </button>
            ) : (
              <span
                className="client-form-view-only"
                data-testid="client-details-view-only"
                title="Only Caregiver Admins can edit client information"
              >
                View only
              </span>
            )}
            {editing && (
              <button type="submit" aria-label="Save client details">
                <SaveIcon />
              </button>
            )}
          </div>
        </div>

        {!canEdit ? (
          <p
            id="client-details-readonly-notice"
            className="client-form-readonly-notice"
            data-testid="client-details-readonly-notice"
            role="note"
          >
            Only Caregiver Admins can edit client information. Contact a
            Caregiver Admin if something needs to be updated.
          </p>
        ) : null}

        {primaryFields.map((field) => (
          <div
            key={field.name}
            className={`client-form-row ${!editing ? "non-edit" : ""}`}
          >
            <label
              htmlFor={field.name}
              className={`client-form-label ${!editing ? "non-edit" : ""}`}
            >
              {field.label}:
            </label>

            <div
              className={`client-form-field ${!editing ? "non-edit" : ""}`}
            >
              {editing ? (
                field.mask ? (
                  <InputMask
                    mask={field.mask}
                    value={formik.values[field.name]}
                    onChange={formik.handleChange}
                  >
                    {(inputProps) => (
                      <input
                        {...inputProps}
                        type="text"
                        id={field.name}
                        name={field.name}
                        className="client-form-input"
                      />
                    )}
                  </InputMask>
                ) : field.type === "select" ? (
                  <select
                    id={field.name}
                    name={field.name}
                    value={formik.values[field.name]}
                    onChange={formik.handleChange}
                    className="client-form-select"
                    required
                  >
                    <option value="" disabled>
                      {field.name === "state"
                        ? "Select state"
                        : "Select timezone"}
                    </option>
                    {field.name === "state"
                      ? US_STATES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))
                      : US_TIMEZONES.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                  </select>
                ) : (
                  <input
                    type={field.type}
                    id={field.name}
                    name={field.name}
                    value={formik.values[field.name]}
                    onChange={formik.handleChange}
                    disabled={field.disabled}
                    className="client-form-input"
                  />
                )
              ) : field.type === "select" ? (
                <span className="client-form-readonly">
                  {field.name === "state"
                    ? displayValues.state
                    : formatClientIanaTzReadable(displayValues[field.name]) ||
                      US_TIMEZONES.find(
                        ({ value }) => value === displayValues[field.name],
                      )?.label}
                </span>
              ) : (
                <span className="client-form-readonly">
                  {displayValues[field.name]}
                </span>
              )}

              {formik.touched[field.name] && formik.errors[field.name] && (
                <small className="client-form-error">
                  {formik.errors[field.name]}
                </small>
              )}
            </div>
          </div>
        ))}

        {!editing && (
          <button
            type="button"
            className="client-form-toggle-more"
            aria-expanded={isExpanded}
            aria-controls="client-details-more-fields"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? "Show less" : "Show more"}
          </button>
        )}

        <div id="client-details-more-fields">
          {showCollapsibleFields &&
            collapsibleFields.map((field) => (
              <div
                key={field.name}
                className={`client-form-row ${!editing ? "non-edit" : ""}`}
              >
                <label
                  htmlFor={field.name}
                  className={`client-form-label ${!editing ? "non-edit" : ""}`}
                >
                  {field.label}:
                </label>

                <div
                  className={`client-form-field ${!editing ? "non-edit" : ""}`}
                >
                  {editing ? (
                    field.mask ? (
                      <InputMask
                        mask={field.mask}
                        value={formik.values[field.name]}
                        onChange={formik.handleChange}
                      >
                        {(inputProps) => (
                          <input
                            {...inputProps}
                            type="text"
                            id={field.name}
                            name={field.name}
                            className="client-form-input"
                          />
                        )}
                      </InputMask>
                    ) : field.type === "select" ? (
                      <select
                        id={field.name}
                        name={field.name}
                        value={formik.values[field.name]}
                        onChange={formik.handleChange}
                        className="client-form-select"
                        required
                      >
                        <option value="" disabled>
                          {field.name === "state"
                            ? "Select state"
                            : "Select timezone"}
                        </option>
                        {field.name === "state"
                          ? US_STATES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))
                          : US_TIMEZONES.map((tz) => (
                            <option key={tz.value} value={tz.value}>
                              {tz.label}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        id={field.name}
                        name={field.name}
                        value={formik.values[field.name]}
                        onChange={formik.handleChange}
                        disabled={field.disabled}
                        className="client-form-input"
                      />
                    )
                  ) : field.type === "select" ? (
                    <span className="client-form-readonly">
                      {field.name === "state"
                        ? displayValues.state
                        : formatClientIanaTzReadable(displayValues[field.name]) ||
                          US_TIMEZONES.find(
                            ({ value }) => value === displayValues[field.name],
                          )?.label}
                    </span>
                  ) : (
                    <span className="client-form-readonly">
                      {displayValues[field.name]}
                    </span>
                  )}

                  {formik.touched[field.name] && formik.errors[field.name] && (
                    <small className="client-form-error">
                      {formik.errors[field.name]}
                    </small>
                  )}
                </div>
              </div>
            ))}
        </div>
      </form>
    </div>
  );
}
